// src/data/languages/yoruba/sentences.ts

interface Sentence {
  original: string;
  french: string;
  english?: string;
  words: string[];
  audio?: string;
  category: string;
  tags: string[];
}

export const SENTENCE_CATEGORIES = {
  'Salutations': {
    icon: '👋',
    description: 'Premiers contacts et formules de politesse',
  },
  'Présentation': {
    icon: '🤝',
    description: 'Se présenter et faire connaissance',
  },
  'Vie quotidienne': {
    icon: '🌞',
    description: 'Expressions du quotidien',
  },
  'Au marché': {
    icon: '🛍️',
    description: 'Faire ses courses et négocier',
  },
  'Voyages': {
    icon: '✈️',
    description: 'Se déplacer et visiter',
  },
  'Famille et relations': {
    icon: '👨‍👩‍👧‍👦',
    description: 'Parler de sa famille et de ses proches',
  },
  'Urgences et santé': {
    icon: '🚨',
    description: 'Expressions pour les situations médicales et urgentes',
  },
  'Loisirs et culture': {
    icon: '🎭',
    description: 'Parler de ses activités, passe-temps et culture',
  },
} as const;

export const sentencesToConstruct: Sentence[] = [
  // ===== SALUTATIONS =====
  {
    original: "Ẹ káàárọ̀",
    french: "Bonjour (matin)",
    english: "Good morning",
    words: ["Ẹ", "káàárọ̀"],
    category: 'Salutations',
    tags: ['salutations', 'matin']
  },
  {
    original: "Ẹ káàsán",
    french: "Bon après-midi",
    english: "Good afternoon",
    words: ["Ẹ", "káàsán"],
    category: 'Salutations',
    tags: ['salutations', 'après-midi']
  },
  {
    original: "Ẹ káalẹ́",
    french: "Bonsoir",
    english: "Good evening",
    words: ["Ẹ", "káalẹ́"],
    category: 'Salutations',
    tags: ['salutations', 'soir']
  },
  {
    original: "Báwo ni?",
    french: "Comment ça va ?",
    english: "How are you?",
    words: ["Báwo", "ni"],
    category: 'Salutations',
    tags: ['salutations', 'question']
  },
  {
    original: "Mo wà dáadáa",
    french: "Je vais bien",
    english: "I am fine",
    words: ["Mo", "wà", "dáadáa"],
    category: 'Salutations',
    tags: ['réponse', 'état']
  },
  {
    original: "Àlàáfíà ni",
    french: "C'est la paix (tout va bien)",
    english: "It's peace (all is well)",
    words: ["Àlàáfíà", "ni"],
    category: 'Salutations',
    tags: ['salutations', 'réponse']
  },
  {
    original: "Ó dàbọ̀",
    french: "Au revoir",
    english: "Goodbye",
    words: ["Ó", "dàbọ̀"],
    category: 'Salutations',
    tags: ['salutations', 'au revoir']
  },

  // ===== PRÉSENTATION =====
  {
    original: "Orúkọ mi ni …",
    french: "Mon nom est / Je m'appelle …",
    english: "My name is …",
    words: ["Orúkọ", "mi", "ni"],
    audio: "Yo-Oruko_mi_ni-2",
    category: 'Présentation',
    tags: ['nom', 'présentation']
  },
  {
    original: "A ń pe mi ní …",
    french: "On m'appelle …",
    english: "I am called …",
    words: ["A", "ń", "pe", "mi", "ní"],
    category: 'Présentation',
    tags: ['nom', 'présentation']
  },
  {
    original: "Kí ni orúkọ rẹ?",
    french: "Comment tu t'appelles ?",
    english: "What is your name?",
    words: ["Kí", "ni", "orúkọ", "rẹ"],
    category: 'Présentation',
    tags: ['présentation', 'question']
  },
  {
    original: "Mo wá láti …",
    french: "Je viens de …",
    english: "I come from …",
    words: ["Mo", "wá", "láti"],
    category: 'Présentation',
    tags: ['origine', 'lieu']
  },
  {
    original: "Níbo ni o ti wá?",
    french: "Tu viens d'où ?",
    english: "Where do you come from?",
    words: ["Níbo", "ni", "o", "ti", "wá"],
    category: 'Présentation',
    tags: ['origine', 'question']
  },
  {
    original: "Mo jẹ́ ọmọ ọdún mẹ́wàá",
    french: "J'ai dix ans",
    english: "I am ten years old",
    words: ["Mo", "jẹ́", "ọmọ", "ọdún", "mẹ́wàá"],
    category: 'Présentation',
    tags: ['âge', 'description']
  },
  {
    original: "Níbo ni o ń gbé?",
    french: "Où habites-tu ?",
    english: "Where do you live?",
    words: ["Níbo", "ni", "o", "ń", "gbé"],
    category: 'Présentation',
    tags: ['lieu', 'question']
  },

  // ===== VIE QUOTIDIENNE =====
  {
    original: "Ó rẹ̀ mí",
    french: "Je suis fatigué (litt. « la fatigue me tient »)",
    english: "I am tired",
    words: ["Ó", "rẹ̀", "mí"],
    category: 'Vie quotidienne',
    tags: ['état', 'fatigue']
  },
  {
    original: "Mo ń jẹun",
    french: "Je mange",
    english: "I am eating",
    words: ["Mo", "ń", "jẹun"],
    category: 'Vie quotidienne',
    tags: ['nourriture', 'activité']
  },
  {
    original: "Òùngbẹ ń gbẹ mí",
    french: "J'ai soif",
    english: "I am thirsty",
    words: ["Òùngbẹ", "ń", "gbẹ", "mí"],
    category: 'Vie quotidienne',
    tags: ['boisson', 'état']
  },
  {
    original: "Aago mélòó ló lù?",
    french: "Quelle heure est-il ?",
    english: "What time is it?",
    words: ["Aago", "mélòó", "ló", "lù"],
    category: 'Vie quotidienne',
    tags: ['temps', 'question']
  },
  {
    original: "Mo ń lọ",
    french: "Je pars / J'y vais",
    english: "I am leaving / I am going",
    words: ["Mo", "ń", "lọ"],
    category: 'Vie quotidienne',
    tags: ['déplacement', 'action']
  },
  {
    original: "Mo ń sùn",
    french: "Je dors",
    english: "I am sleeping",
    words: ["Mo", "ń", "sùn"],
    category: 'Vie quotidienne',
    tags: ['repos', 'activité']
  },
  {
    original: "Mo ń ṣe iṣẹ́ ilé",
    french: "Je fais les tâches ménagères",
    english: "I am doing the housework",
    words: ["Mo", "ń", "ṣe", "iṣẹ́", "ilé"],
    category: 'Vie quotidienne',
    tags: ['maison', 'activité']
  },

  // ===== AU MARCHÉ =====
  {
    original: "Èló ni?",
    french: "Combien ça coûte ?",
    english: "How much does it cost?",
    words: ["Èló", "ni"],
    category: 'Au marché',
    tags: ['prix', 'question']
  },
  {
    original: "Mo ń wá …",
    french: "Je cherche …",
    english: "I am looking for …",
    words: ["Mo", "ń", "wá"],
    category: 'Au marché',
    tags: ['achat', 'recherche']
  },
  {
    original: "Ó wọ́n jù",
    french: "C'est trop cher",
    english: "It's too expensive",
    words: ["Ó", "wọ́n", "jù"],
    category: 'Au marché',
    tags: ['prix', 'négociation']
  },
  {
    original: "Ẹ dín owó rẹ̀ kù",
    french: "Baissez le prix",
    english: "Lower the price",
    words: ["Ẹ", "dín", "owó", "rẹ̀", "kù"],
    category: 'Au marché',
    tags: ['prix', 'négociation']
  },
  {
    original: "Mo fẹ́ ra …",
    french: "Je veux acheter …",
    english: "I want to buy …",
    words: ["Mo", "fẹ́", "ra"],
    category: 'Au marché',
    tags: ['achat', 'intention']
  },
  {
    original: "Kò sí",
    french: "Il n'y en a pas",
    english: "There isn't any",
    words: ["Kò", "sí"],
    category: 'Au marché',
    tags: ['disponibilité', 'négation']
  },

  // ===== VOYAGES =====
  {
    original: "Mo ń lọ sí …",
    french: "Je vais à …",
    english: "I am going to …",
    words: ["Mo", "ń", "lọ", "sí"],
    category: 'Voyages',
    tags: ['déplacement', 'destination']
  },
  {
    original: "Níbo ni tákísì wà?",
    french: "Où est le taxi ?",
    english: "Where is the taxi?",
    words: ["Níbo", "ni", "tákísì", "wà"],
    category: 'Voyages',
    tags: ['transport', 'lieu']
  },
  {
    original: "Níbo ni awakọ̀ wà?",
    french: "Où est le chauffeur ?",
    english: "Where is the driver?",
    words: ["Níbo", "ni", "awakọ̀", "wà"],
    category: 'Voyages',
    tags: ['transport', 'personne']
  },
  {
    original: "Mo fẹ́ ra tikẹ́ẹ̀tì",
    french: "Je veux un billet",
    english: "I want a ticket",
    words: ["Mo", "fẹ́", "ra", "tikẹ́ẹ̀tì"],
    category: 'Voyages',
    tags: ['transport', 'document']
  },
  {
    original: "Ó jìnà",
    french: "C'est loin",
    english: "It's far",
    words: ["Ó", "jìnà"],
    category: 'Voyages',
    tags: ['distance', 'description']
  },

  // ===== FAMILLE ET RELATIONS =====
  {
    original: "Ìyá mi",
    french: "Ma mère",
    english: "My mother",
    words: ["Ìyá", "mi"],
    category: 'Famille et relations',
    tags: ['famille', 'parent']
  },
  {
    original: "Bàbá mi",
    french: "Mon père",
    english: "My father",
    words: ["Bàbá", "mi"],
    category: 'Famille et relations',
    tags: ['famille', 'parent']
  },
  {
    original: "Ọkọ mi",
    french: "Mon mari",
    english: "My husband",
    words: ["Ọkọ", "mi"],
    category: 'Famille et relations',
    tags: ['famille', 'conjoint']
  },
  {
    original: "Aya mi",
    french: "Ma femme",
    english: "My wife",
    words: ["Aya", "mi"],
    category: 'Famille et relations',
    tags: ['famille', 'conjoint']
  },
  {
    original: "Mo ní ọmọ méjì",
    french: "J'ai deux enfants",
    english: "I have two children",
    words: ["Mo", "ní", "ọmọ", "méjì"],
    category: 'Famille et relations',
    tags: ['famille', 'enfant']
  },
  {
    original: "Ìyá ńlá mi",
    french: "Ma grand-mère",
    english: "My grandmother",
    words: ["Ìyá", "ńlá", "mi"],
    category: 'Famille et relations',
    tags: ['famille', 'grand-parent']
  },

  // ===== URGENCES ET SANTÉ =====
  {
    original: "Ẹ gbà mí!",
    french: "Au secours ! / À l'aide !",
    english: "Help!",
    words: ["Ẹ", "gbà", "mí"],
    category: 'Urgences et santé',
    tags: ['urgence', 'aide']
  },
  {
    original: "Ara mi ò yá",
    french: "Je ne me sens pas bien / Je suis malade",
    english: "I don't feel well / I am sick",
    words: ["Ara", "mi", "ò", "yá"],
    category: 'Urgences et santé',
    tags: ['santé', 'état']
  },
  {
    original: "Mo nílò dókítà",
    french: "J'ai besoin d'un docteur",
    english: "I need a doctor",
    words: ["Mo", "nílò", "dókítà"],
    category: 'Urgences et santé',
    tags: ['santé', 'médecin']
  },
  {
    original: "Níbo ni ilé ìwòsàn wà?",
    french: "Où est l'hôpital ?",
    english: "Where is the hospital?",
    words: ["Níbo", "ni", "ilé", "ìwòsàn", "wà"],
    category: 'Urgences et santé',
    tags: ['santé', 'lieu', 'urgence']
  },
  {
    original: "Orí ń fọ́ mi",
    french: "J'ai mal à la tête (litt. « la tête me brise »)",
    english: "I have a headache",
    words: ["Orí", "ń", "fọ́", "mi"],
    audio: "Yo-Ori_n_fo_mi",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'tête']
  },
  {
    original: "Mo nílò òògùn",
    french: "J'ai besoin de médicaments",
    english: "I need medicine",
    words: ["Mo", "nílò", "òògùn"],
    category: 'Urgences et santé',
    tags: ['santé', 'médicament']
  },

  // ===== LOISIRS ET CULTURE =====
  {
    original: "Mo fẹ́ràn orin",
    french: "J'aime la musique",
    english: "I like music",
    words: ["Mo", "fẹ́ràn", "orin"],
    category: 'Loisirs et culture',
    tags: ['loisirs', 'musique', 'goûts']
  },
  {
    original: "Mo ń gbá bọ́ọ̀lù",
    french: "Je joue au football",
    english: "I am playing football",
    words: ["Mo", "ń", "gbá", "bọ́ọ̀lù"],
    category: 'Loisirs et culture',
    tags: ['sport', 'football', 'jeu']
  },
  {
    original: "Ẹ jẹ́ ká jọ kọrin",
    french: "Chantons ensemble",
    english: "Let's sing together",
    words: ["Ẹ", "jẹ́", "ká", "jọ", "kọrin"],
    category: 'Loisirs et culture',
    tags: ['musique', 'social', 'chant']
  },
  {
    original: "Mo ń ka ìwé",
    french: "Je lis un livre",
    english: "I am reading a book",
    words: ["Mo", "ń", "ka", "ìwé"],
    category: 'Loisirs et culture',
    tags: ['lecture', 'livre', 'culture']
  },
  {
    original: "Ẹ jẹ́ ká lọ sí sinimá",
    french: "Allons au cinéma",
    english: "Let's go to the cinema",
    words: ["Ẹ", "jẹ́", "ká", "lọ", "sí", "sinimá"],
    category: 'Loisirs et culture',
    tags: ['cinéma', 'sortie', 'culture']
  },
  {
    original: "Mo fẹ́ràn eré",
    french: "J'aime les jeux",
    english: "I like games",
    words: ["Mo", "fẹ́ràn", "eré"],
    category: 'Loisirs et culture',
    tags: ['jeu', 'loisirs', 'plaisir']
  },
  // ===== PHRASES AVEC AUDIO (liste Yo-) =====
  // --- Salutations ---
  {
    original: "Pẹlẹ o!",
    french: "Salut!",
    english: "Hi!",
    words: ["Pẹlẹ", "o"],
    audio: "Yo-Pele_o",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Mo ki gbogbo yin o!",
    french: "Salut à tous!",
    english: "Hello everyone!",
    words: ["Mo", "ki", "gbogbo", "yin", "o"],
    audio: "Yo-Mo_ki_gbogbo_yin_o",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Ẹ kaarọ!",
    french: "Bonjour!",
    english: "Good morning!",
    words: ["Ẹ", "kaarọ"],
    audio: "Yo-E_kaaro",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "O tojọ mẹta!",
    french: "Ça faisait longtemps! (qu'on ne s'est pas vu)",
    english: "Long time no see!",
    words: ["O", "tojọ", "mẹta"],
    audio: "Yo-O_tojo_meta",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Inu mi dun lati mọ yin!",
    french: "Je suis heureux (heureuse) de te rencontrer",
    english: "Nice to meet you!",
    words: ["Inu", "mi", "dun", "lati", "mọ", "yin"],
    audio: "Yo-Inu_mi_dun_lati_mo_yin",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Ma a ri ẹ laipẹ!",
    french: "À plus tard! / À bientôt!",
    english: "See you later! / See you soon!",
    words: ["Ma", "a", "ri", "ẹ", "laipẹ"],
    audio: "Yo-Ma_a_ri_e_laipe",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "O dabọ!",
    french: "Au revoir!",
    english: "Goodbye!",
    words: ["O", "dabọ"],
    audio: "Yo-O_dabo",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Mo ma a ri ọ ni ọla",
    french: "À demain!",
    english: "See you tomorrow!",
    words: ["Mo", "ma", "a", "ri", "ọ", "ni", "ọla"],
    audio: "Yo-Mo_ma_a_ri_o_ni_ola",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "A dupẹ",
    french: "Merci",
    english: "Thank you.",
    words: ["A", "dupẹ"],
    audio: "Yo-A_dupe",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "O ku ọjọ ibi!",
    french: "Joyeux anniversaire!",
    english: "Happy birthday!",
    words: ["O", "ku", "ọjọ", "ibi"],
    audio: "Yo-O_ku_ojo_ibi",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Gbadun ipari ọse",
    french: "Bon week-end!",
    english: "Have a nice weekend!",
    words: ["Gbadun", "ipari", "ọse"],
    audio: "Yo-Gbadun_ipari_ose",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Asọ kale ayọ",
    french: "Bon voyage!",
    english: "Have a good trip!",
    words: ["Asọ", "kale", "ayọ"],
    audio: "Yo-Aso_kale_ayo",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Bawo ni?",
    french: "Comment vas-tu?",
    english: "How are you?",
    words: ["Bawo", "ni"],
    audio: "Yo-Bawo_ni",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  {
    original: "Daada ni mo wa",
    french: "Je vais bien",
    english: "I'm fine.",
    words: ["Daada", "ni", "mo", "wa"],
    audio: "Yo-Daada_ni_mo_wa",
    category: 'Salutations',
    tags: ['salutations', 'politesse']
  },
  // --- Présentation ---
  {
    original: "Ki ni orukọ rẹ?",
    french: "Comment t'appelles-tu?",
    english: "What's your name?",
    words: ["Ki", "ni", "orukọ", "rẹ"],
    audio: "Yo-Ki_ni_oruko_re",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Orukọ mi ni …",
    french: "Mon nom est …",
    english: "My name is …",
    words: ["Orukọ", "mi", "ni"],
    audio: "Yo-Oruko_mi_ni",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Tani ọ?",
    french: "Qui es-tu?",
    english: "Who are you?",
    words: ["Tani", "ọ"],
    audio: "Yo-Tani_o",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Akekọ ni mi",
    french: "Je suis étudiant(e)",
    english: "I'm a student.",
    words: ["Akekọ", "ni", "mi"],
    audio: "Yo-Akeko_ni_mi",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Mo n ṣiṣẹ ni ile-iwosan",
    french: "Je travaille dans un hôpital",
    english: "I work in a hospital.",
    words: ["Mo", "n", "ṣiṣẹ", "ni", "ile-iwosan"],
    audio: "Yo-Mo_n_sise_ni_ile-iwosan",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Ilu wo ni o ti wa?",
    french: "De quel pays viens-tu?",
    english: "Which country are you from?",
    words: ["Ilu", "wo", "ni", "o", "ti", "wa"],
    audio: "Yo-Ilu_wo_ni_o_ti_wa",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Ọmọ ilu Jẹmani ni mi",
    french: "Je suis allemand(e)",
    english: "I'm German.",
    words: ["Ọmọ", "ilu", "Jẹmani", "ni", "mi"],
    audio: "Yo-Omo_ilu_Jemani_ni_mi",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Ọmọ ilu Spain ni mi",
    french: "Je suis espagnol(e)",
    english: "I'm Spanish.",
    words: ["Ọmọ", "ilu", "Spain", "ni", "mi"],
    audio: "Yo-Omo_ilu_Spain_ni_mi",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Ọmọ ilu Mẹksiko ni mi",
    french: "Je suis mexicain(e)",
    english: "I'm Mexican.",
    words: ["Ọmọ", "ilu", "Mẹksiko", "ni", "mi"],
    audio: "Yo-Omo_ilu_Meksiko_ni_mi",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Ọmọ ilu Faranse ni mi",
    french: "Je suis français(e)",
    english: "I'm French.",
    words: ["Ọmọ", "ilu", "Faranse", "ni", "mi"],
    audio: "Yo-Omo_ilu_Faranse_ni_mi",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Ọmọ ọdun melo ni ẹ?",
    french: "Quel âge as-tu?",
    english: "How old are you?",
    words: ["Ọmọ", "ọdun", "melo", "ni", "ẹ"],
    audio: "Yo-Omo_odun_melo_ni_e",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  {
    original: "Ọmọ ọdun marundinlogun ni mi",
    french: "J'ai 25 ans",
    english: "I'm 25 years old.",
    words: ["Ọmọ", "ọdun", "marundinlogun", "ni", "mi"],
    audio: "Yo-Omo_odun_marundinlogun_ni_mi",
    category: 'Présentation',
    tags: ['présentation', 'identité']
  },
  // --- Vie quotidienne ---
  {
    original: "Ki ni ago wi?",
    french: "Quelle heure est-il?",
    english: "What time is it?",
    words: ["Ki", "ni", "ago", "wi"],
    audio: "Yo-Ki_ni_ago_wi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ago mẹrin ọsan ni",
    french: "Il est quatre heures de l'après-midi",
    english: "It's four in the afternoon.",
    words: ["Ago", "mẹrin", "ọsan", "ni"],
    audio: "Yo-Ago_merin_osan_ni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Loni emi ko ni aye lati …",
    french: "Aujourd'hui, je n'ai pas le temps de …",
    english: "Today I don't have time to …",
    words: ["Loni", "emi", "ko", "ni", "aye", "lati"],
    audio: "Yo-Loni_emi_ko_ni_aye_lati",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ọla ni ọjọ ibi mi",
    french: "Demain, c'est mon anniversaire",
    english: "Tomorrow is my birthday.",
    words: ["Ọla", "ni", "ọjọ", "ibi", "mi"],
    audio: "Yo-Ola_ni_ojo_ibi_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ni ọdun yii emi ko fẹ ṣayẹyẹ ọjọ ibi mi",
    french: "Cette année, je ne veux pas fêter mon anniversaire",
    english: "This year I don't want to celebrate my birthday.",
    words: ["Ni", "ọdun", "yii", "emi", "ko", "fẹ", "ṣayẹyẹ", "ọjọ", "ibi", "mi"],
    audio: "Yo-Ni_odun_yii_emi_ko_fe_sayeye_ojo_ibi_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Eni ni ọjọ kesan ninu oṣu Kẹsan",
    french: "Aujourd'hui, on est le 9 septembre",
    english: "Today is September 9th.",
    words: ["Eni", "ni", "ọjọ", "kesan", "ninu", "oṣu", "Kẹsan"],
    audio: "Yo-Eni_ni_ojo_kesan_ninu_osu_Kesan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ọjọ wo ni oni jẹ?",
    french: "Quel jour est-on aujourd'hui?",
    english: "What day is it today?",
    words: ["Ọjọ", "wo", "ni", "oni", "jẹ"],
    audio: "Yo-Ojo_wo_ni_oni_je",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Oni ni ọjọ Iṣẹgun",
    french: "Aujourd'hui, c'est mardi",
    english: "Today is Tuesday.",
    words: ["Oni", "ni", "ọjọ", "Iṣẹgun"],
    audio: "Yo-Oni_ni_ojo_Isegun",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ọna mi pọ loni",
    french: "Aujourd'hui, je suis très occupé(e)",
    english: "I'm very busy today.",
    words: ["Ọna", "mi", "pọ", "loni"],
    audio: "Yo-Ona_mi_po_loni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ki ni o tiiṣe loni?",
    french: "Qu'est-ce que tu as fait aujourd'hui?",
    english: "What did you do today?",
    words: ["Ki", "ni", "o", "tiiṣe", "loni"],
    audio: "Yo-Ki_ni_o_tiise_loni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ọla ni ọjọ Ru",
    french: "Demain, c'est mercredi",
    english: "Tomorrow is Wednesday.",
    words: ["Ọla", "ni", "ọjọ", "Ru"],
    audio: "Yo-Ola_ni_ojo_Ru",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "kini o ma ṣe ni ọla?",
    french: "Que feras-tu demain?",
    english: "What will you do tomorrow?",
    words: ["kini", "o", "ma", "ṣe", "ni", "ọla"],
    audio: "Yo-kini_o_ma_se_ni_ola",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Kini o fẹ ṣe ni ọla?",
    french: "Qu'est-ce que tu veux faire demain?",
    english: "What do you want to do tomorrow?",
    words: ["Kini", "o", "fẹ", "ṣe", "ni", "ọla"],
    audio: "Yo-Kini_o_fe_se_ni_ola",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Ni ọla mo fẹ pade pẹlu baba mi",
    french: "Demain, je veux voir mon père",
    english: "Tomorrow I want to see my father.",
    words: ["Ni", "ọla", "mo", "fẹ", "pade", "pẹlu", "baba", "mi"],
    audio: "Yo-Ni_ola_mo_fe_pade_pelu_baba_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Kini o ṣe ni ana?",
    french: "Qu'est-ce que tu as fait hier?",
    english: "What did you do yesterday?",
    words: ["Kini", "o", "ṣe", "ni", "ana"],
    audio: "Yo-Kini_o_se_ni_ana",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'temps', 'calendrier']
  },
  {
    original: "Jọwọ ṣe o le sọrọ diẹdiẹ?",
    french: "Peux-tu parler un peu moins vite, s'il-te-plaît?",
    english: "Can you speak a little more slowly, please?",
    words: ["Jọwọ", "ṣe", "o", "le", "sọrọ", "diẹdiẹ"],
    audio: "Yo-Jowo_se_o_le_soro_diedie",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Jọwọ ṣe o le tun ọrọ naa sọ?",
    french: "Peux-tu répéter la phrase, s'il-te-plaît?",
    english: "Can you repeat the sentence, please?",
    words: ["Jọwọ", "ṣe", "o", "le", "tun", "ọrọ", "naa", "sọ"],
    audio: "Yo-Jowo_se_o_le_tun_oro_naa_so",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Jọwọ ṣe o le tun sọ?",
    french: "Peux-tu répéter, s'il-te-plaît?",
    english: "Can you repeat, please?",
    words: ["Jọwọ", "ṣe", "o", "le", "tun", "sọ"],
    audio: "Yo-Jowo_se_o_le_tun_so",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Jọwọ ṣe o le kọ silẹ?",
    french: "Peux-tu l'écrire, s'il-te-plaît?",
    english: "Can you write it down, please?",
    words: ["Jọwọ", "ṣe", "o", "le", "kọ", "silẹ"],
    audio: "Yo-Jowo_se_o_le_ko_sile",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Jọwọ ṣe o le sọ ọrọ naa labidi?",
    french: "Peux-tu m'épeler ce mot, s'il-te-plaît?",
    english: "Can you spell this word for me, please?",
    words: ["Jọwọ", "ṣe", "o", "le", "sọ", "ọrọ", "naa", "labidi"],
    audio: "Yo-Jowo_se_o_le_so_oro_naa_labidi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Ṣe o le ṣalaye ohun ti ọrọ yi jẹ?",
    french: "Peux-tu m'expliquer la signification de ce mot, s'il-te-plaît?",
    english: "Can you explain what this word means, please?",
    words: ["Ṣe", "o", "le", "ṣalaye", "ohun", "ti", "ọrọ", "yi", "jẹ"],
    audio: "Yo-Se_o_le_salaye_ohun_ti_oro_yi_je",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Nko gbọ",
    french: "Je n'ai pas entendu",
    english: "I didn't hear.",
    words: ["Nko", "gbọ"],
    audio: "Yo-Nko_gbo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Kini itumọ …?",
    french: "Que veut dire …?",
    english: "What does … mean?",
    words: ["Kini", "itumọ"],
    audio: "Yo-Kini_itumo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Kò yé mi",
    french: "Je ne comprends pas",
    english: "I don't understand.",
    words: ["Kò", "yé", "mi"],
    audio: "Yo-Ko_ye_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Nkan yi o ye mi",
    french: "Je ne comprends pas cette expression",
    english: "I don't understand this expression.",
    words: ["Nkan", "yi", "o", "ye", "mi"],
    audio: "Yo-Nkan_yi_o_ye_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Mo ni ibeere",
    french: "J'ai une question",
    english: "I have a question.",
    words: ["Mo", "ni", "ibeere"],
    audio: "Yo-Mo_ni_ibeere",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Ṣe mo le bi ọ ni ibeere kan?",
    french: "Est-ce que je peux te poser une question?",
    english: "Can I ask you a question?",
    words: ["Ṣe", "mo", "le", "bi", "ọ", "ni", "ibeere", "kan"],
    audio: "Yo-Se_mo_le_bi_o_ni_ibeere_kan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Nko mọ",
    french: "Je ne sais pas",
    english: "I don't know.",
    words: ["Nko", "mọ"],
    audio: "Yo-Nko_mo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Maṣe yọ nu",
    french: "Ce n'est pas grave",
    english: "It doesn't matter.",
    words: ["Maṣe", "yọ", "nu"],
    audio: "Yo-Mase_yo_nu",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Jọwọ tunnsọ!",
    french: "Répète, s'il-te-plaît",
    english: "Repeat, please.",
    words: ["Jọwọ", "tunnsọ"],
    audio: "Yo-Jowo_tunnso",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Ma binu wipe mo damu ọ, jọwọ tẹsiwaju",
    french: "Je suis vraiment désolé(e) de te déranger, continue, s'il-te-plaît",
    english: "I'm really sorry to disturb you, please go on.",
    words: ["Ma", "binu", "wipe", "mo", "damu", "ọ", "jọwọ", "tẹsiwaju"],
    audio: "Yo-Ma_binu_wipe_mo_damu_o_jowo_tesiwaju",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Kini o ṣẹ sọ tan?",
    french: "Qu'est-ce que tu viens de dire?",
    english: "What did you just say?",
    words: ["Kini", "o", "ṣẹ", "sọ", "tan"],
    audio: "Yo-Kini_o_se_so_tan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Inu mi dun gan ni",
    french: "Je suis très heureux (heureuse)",
    english: "I'm very happy.",
    words: ["Inu", "mi", "dun", "gan", "ni"],
    audio: "Yo-Inu_mi_dun_gan_ni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'émotions']
  },
  {
    original: "Orẹ mi gan ni",
    french: "Je suis très fatigué(e)",
    english: "I'm very tired.",
    words: ["Orẹ", "mi", "gan", "ni"],
    audio: "Yo-Ore_mi_gan_ni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'émotions']
  },
  {
    original: "Inu mi bajẹ gan ni",
    french: "Je suis très triste",
    english: "I'm very sad.",
    words: ["Inu", "mi", "bajẹ", "gan", "ni"],
    audio: "Yo-Inu_mi_baje_gan_ni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'émotions']
  },
  {
    original: "Idamu ọkan ba mi gan",
    french: "Je suis très inquiet (inquiète)",
    english: "I'm very worried.",
    words: ["Idamu", "ọkan", "ba", "mi", "gan"],
    audio: "Yo-Idamu_okan_ba_mi_gan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'émotions']
  },
  {
    original: "Mo ni idamu ọkan pupọ",
    french: "J'ai beaucoup de soucis",
    english: "I have a lot of worries.",
    words: ["Mo", "ni", "idamu", "ọkan", "pupọ"],
    audio: "Yo-Mo_ni_idamu_okan_pupo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'émotions']
  },
  {
    original: "Ma ronu!",
    french: "Ne t'inquiète pas!",
    english: "Don't worry!",
    words: ["Ma", "ronu"],
    audio: "Yo-Ma_ronu",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'émotions']
  },
  {
    original: "Ongbẹ gbẹ mi",
    french: "J'ai soif",
    english: "I'm thirsty.",
    words: ["Ongbẹ", "gbẹ", "mi"],
    audio: "Yo-Ongbe_gbe_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Kini o ma fẹran lati mu",
    french: "Qu'est-ce que tu veux boire?",
    english: "What do you want to drink?",
    words: ["Kini", "o", "ma", "fẹran", "lati", "mu"],
    audio: "Yo-Kini_o_ma_feran_lati_mu",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo ma fẹran lati mu omi",
    french: "J'aimerais bien boire de l'eau",
    english: "I'd like to drink some water.",
    words: ["Mo", "ma", "fẹran", "lati", "mu", "omi"],
    audio: "Yo-Mo_ma_feran_lati_mu_omi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ṣe o fẹran lati mu ọti oyinbo",
    french: "Est-ce que tu aimes boire du vin?",
    english: "Do you like drinking wine?",
    words: ["Ṣe", "o", "fẹran", "lati", "mu", "ọti", "oyinbo"],
    audio: "Yo-Se_o_feran_lati_mu_oti_oyinbo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Jọwọ, kọfi kan",
    french: "Un café, s'il te plaît",
    english: "A coffee, please.",
    words: ["Jọwọ", "kọfi", "kan"],
    audio: "Yo-Jowo_kofi_kan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo ma nifẹ si kọpu kọfi kan",
    french: "J'aimerais bien un café",
    english: "I'd like a coffee.",
    words: ["Mo", "ma", "nifẹ", "si", "kọpu", "kọfi", "kan"],
    audio: "Yo-Mo_ma_nife_si_kopu_kofi_kan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ṣe o fẹran lati mu tii?",
    french: "Est-ce que tu aimes boire du thé?",
    english: "Do you like drinking tea?",
    words: ["Ṣe", "o", "fẹran", "lati", "mu", "tii"],
    audio: "Yo-Se_o_feran_lati_mu_tii",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ṣe o ma fẹran lati mu tii tabi omi?",
    french: "Est-ce que tu préfères boire du thé ou de l'eau?",
    english: "Would you rather drink tea or water?",
    words: ["Ṣe", "o", "ma", "fẹran", "lati", "mu", "tii", "tabi", "omi"],
    audio: "Yo-Se_o_ma_feran_lati_mu_tii_tabi_omi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ongbẹ ko gbẹ mi",
    french: "Je n'ai pas soif",
    english: "I'm not thirsty.",
    words: ["Ongbẹ", "ko", "gbẹ", "mi"],
    audio: "Yo-Ongbe_ko_gbe_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ki ni o ma fẹran lati jẹ ni irọlẹ yii?",
    french: "Qu'est-ce que tu veux manger ce soir?",
    english: "What do you want to eat tonight?",
    words: ["Ki", "ni", "o", "ma", "fẹran", "lati", "jẹ", "ni", "irọlẹ", "yii"],
    audio: "Yo-Ki_ni_o_ma_feran_lati_je_ni_irole_yii",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ṣe nkan to je to ẹ?",
    french: "Est-ce que tu as assez mangé?",
    english: "Have you eaten enough?",
    words: ["Ṣe", "nkan", "to", "je", "to", "ẹ"],
    audio: "Yo-Se_nkan_to_je_to_e",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo ti yo, iwọ nkọ?",
    french: "J'ai assez mangé, et toi?",
    english: "I've eaten enough, and you?",
    words: ["Mo", "ti", "yo", "iwọ", "nkọ"],
    audio: "Yo-Mo_ti_yo_iwo_nko",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ebi n pa mi",
    french: "J'ai très faim",
    english: "I'm very hungry.",
    words: ["Ebi", "n", "pa", "mi"],
    audio: "Yo-Ebi_n_pa_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo fẹ jẹun",
    french: "Je veux manger",
    english: "I want to eat.",
    words: ["Mo", "fẹ", "jẹun"],
    audio: "Yo-Mo_fe_jeun",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Kini o ma nifẹ lati jẹ?",
    french: "Qu'est-ce que tu veux manger?",
    english: "What do you want to eat?",
    words: ["Kini", "o", "ma", "nifẹ", "lati", "jẹ"],
    audio: "Yo-Kini_o_ma_nife_lati_je",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo ma nifẹ lati jẹ saladi",
    french: "Je voudrais manger une salade",
    english: "I'd like to eat a salad.",
    words: ["Mo", "ma", "nifẹ", "lati", "jẹ", "saladi"],
    audio: "Yo-Mo_ma_nife_lati_je_saladi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo ti jẹun",
    french: "J'ai déjà mangé",
    english: "I've already eaten.",
    words: ["Mo", "ti", "jẹun"],
    audio: "Yo-Mo_ti_jeun",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo ma nifẹ lati jẹ nudulu",
    french: "Je voudrais manger des nouilles / pâtes",
    english: "I'd like to eat noodles / pasta.",
    words: ["Mo", "ma", "nifẹ", "lati", "jẹ", "nudulu"],
    audio: "Yo-Mo_ma_nife_lati_je_nudulu",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Mo ma fẹ jẹ ọbe",
    french: "Je voudrais manger une soupe",
    english: "I'd like to eat some soup.",
    words: ["Mo", "ma", "fẹ", "jẹ", "ọbe"],
    audio: "Yo-Mo_ma_fe_je_obe",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Ebi o pa mi",
    french: "Je n'ai pas faim",
    english: "I'm not hungry.",
    words: ["Ebi", "o", "pa", "mi"],
    audio: "Yo-Ebi_o_pa_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'boisson', 'nourriture']
  },
  {
    original: "Bawo ni oju ọjọ se ri loni?",
    french: "Quel temps fait-il aujourd'hui?",
    english: "What's the weather like today?",
    words: ["Bawo", "ni", "oju", "ọjọ", "se", "ri", "loni"],
    audio: "Yo-Bawo_ni_oju_ojo_se_ri_loni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'météo']
  },
  {
    original: "Ṣe ojo ma rọ loni?",
    french: "Est-ce qu'il va pleuvoir aujourd'hui?",
    english: "Is it going to rain today?",
    words: ["Ṣe", "ojo", "ma", "rọ", "loni"],
    audio: "Yo-Se_ojo_ma_ro_loni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'météo']
  },
  {
    original: "Oni n gbona gan",
    french: "Aujourd'hui, il fait très chaud",
    english: "It's very hot today.",
    words: ["Oni", "n", "gbona", "gan"],
    audio: "Yo-Oni_n_gbona_gan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'météo']
  },
  {
    original: "Oorun n ran loni",
    french: "Aujourd'hui, il y a du soleil",
    english: "It's sunny today.",
    words: ["Oorun", "n", "ran", "loni"],
    audio: "Yo-Oorun_n_ran_loni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'météo']
  },
  {
    original: "Oju ọjọ daara gan loni",
    french: "Il fait très beau aujourd'hui",
    english: "The weather is very nice today.",
    words: ["Oju", "ọjọ", "daara", "gan", "loni"],
    audio: "Yo-Oju_ojo_daara_gan_loni",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'météo']
  },
  {
    original: "Kini yẹn?",
    french: "Qu'est-ce que c'est?",
    english: "What is it?",
    words: ["Kini", "yẹn"],
    audio: "Yo-Kini_yen",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "Tani eleyi?",
    french: "Qui est-ce?",
    english: "Who is it?",
    words: ["Tani", "eleyi"],
    audio: "Yo-Tani_eleyi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "Tani ẹni ti o ni aja yii?",
    french: "Qui est le propriétaire de ce chien?",
    english: "Who owns this dog?",
    words: ["Tani", "ẹni", "ti", "o", "ni", "aja", "yii"],
    audio: "Yo-Tani_eni_ti_o_ni_aja_yii",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "Kini o ṣe?",
    french: "Qu'est-ce que tu as fait?",
    english: "What did you do?",
    words: ["Kini", "o", "ṣe"],
    audio: "Yo-Kini_o_se",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "Nibo ni o tin ṣiṣẹ?",
    french: "Où travailles-tu?",
    english: "Where do you work?",
    words: ["Nibo", "ni", "o", "tin", "ṣiṣẹ"],
    audio: "Yo-Nibo_ni_o_tin_sise",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "O ma n ṣiṣẹ ju",
    french: "Tu travailles trop",
    english: "You work too much.",
    words: ["O", "ma", "n", "ṣiṣẹ", "ju"],
    audio: "Yo-O_ma_n_sise_ju",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "Bawo ni o ṣe mọ?",
    french: "Comment le sais-tu?",
    english: "How do you know?",
    words: ["Bawo", "ni", "o", "ṣe", "mọ"],
    audio: "Yo-Bawo_ni_o_se_mo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "Nibo ni o lọ ni irọlẹ ana?",
    french: "Où es-tu allé(e) hier soir?",
    english: "Where did you go last night?",
    words: ["Nibo", "ni", "o", "lọ", "ni", "irọlẹ", "ana"],
    audio: "Yo-Nibo_ni_o_lo_ni_irole_ana",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  {
    original: "Jọwọ, wọle",
    french: "Entre, s'il-te-plaît",
    english: "Come in, please.",
    words: ["Jọwọ", "wọle"],
    audio: "Yo-Jowo_wole",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ pa ilẹkun de",
    french: "Ferme la porte, s'il-te-plaît",
    english: "Close the door, please.",
    words: ["Jọwọ", "pa", "ilẹkun", "de"],
    audio: "Yo-Jowo_pa_ilekun_de",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ joko",
    french: "Assieds-toi, s'il-te-plaît",
    english: "Sit down, please.",
    words: ["Jọwọ", "joko"],
    audio: "Yo-Jowo_joko",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Farabalẹ!",
    french: "Calme-toi!",
    english: "Calm down!",
    words: ["Farabalẹ"],
    audio: "Yo-Farabale",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ dakẹ!",
    french: "Silence, s'il-te-plaît",
    english: "Quiet, please!",
    words: ["Jọwọ", "dakẹ"],
    audio: "Yo-Jowo_dake",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Ma sọrọ",
    french: "Ne parle pas, s'il-te-plaît",
    english: "Don't talk, please.",
    words: ["Ma", "sọrọ"],
    audio: "Yo-Ma_soro",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ tẹtisi",
    french: "Écoute, s'il-te-plaît",
    english: "Listen, please.",
    words: ["Jọwọ", "tẹtisi"],
    audio: "Yo-Jowo_tetisi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ ma pa ariwo",
    french: "Ne fais pas autant de bruit, s'il-te-plaît",
    english: "Don't make so much noise, please.",
    words: ["Jọwọ", "ma", "pa", "ariwo"],
    audio: "Yo-Jowo_ma_pa_ariwo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Ma sun ẹkun mọ",
    french: "Arrête de pleurer!",
    english: "Stop crying!",
    words: ["Ma", "sun", "ẹkun", "mọ"],
    audio: "Yo-Ma_sun_ekun_mo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Kilode ti o n rẹrin?",
    french: "Pourquoi ris-tu?",
    english: "Why are you laughing?",
    words: ["Kilode", "ti", "o", "n", "rẹrin"],
    audio: "Yo-Kilode_ti_o_n_rerin",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ duro de mi fun igba diẹ, mo ma pada de laarin iṣẹju kan",
    french: "Attends-moi un moment, je reviens tout de suite",
    english: "Wait for me a moment, I'll be right back.",
    words: ["Jọwọ", "duro", "de", "mi", "fun", "igba", "diẹ", "mo", "ma", "pada", "de", "laarin", "iṣẹju", "kan"],
    audio: "Yo-Jowo_duro_de_mi_fun_igba_die_mo_ma_pada_de_laarin_iseju_kan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jẹ ki a simi fun iṣẹju mẹwa",
    french: "Reposons-nous 10 minutes",
    english: "Let's rest for 10 minutes.",
    words: ["Jẹ", "ki", "a", "simi", "fun", "iṣẹju", "mẹwa"],
    audio: "Yo-Je_ki_a_simi_fun_iseju_mewa",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Wọn ko gba ciga mimu laaye!",
    french: "Il est interdit de fumer",
    english: "Smoking is not allowed!",
    words: ["Wọn", "ko", "gba", "ciga", "mimu", "laaye"],
    audio: "Yo-Won_ko_gba_ciga_mimu_laaye",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jẹ ki a jọ lọ",
    french: "Allons-y ensemble",
    english: "Let's go together.",
    words: ["Jẹ", "ki", "a", "jọ", "lọ"],
    audio: "Yo-Je_ki_a_jo_lo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ lọra diẹ rin",
    french: "Marche moins vite, s'il-te-plaît",
    english: "Walk more slowly, please.",
    words: ["Jọwọ", "lọra", "diẹ", "rin"],
    audio: "Yo-Jowo_lora_die_rin",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Maṣe fi akoko mi ṣofo",
    french: "Ne perds pas de temps!",
    english: "Don't waste time!",
    words: ["Maṣe", "fi", "akoko", "mi", "ṣofo"],
    audio: "Yo-Mase_fi_akoko_mi_sofo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Jọwọ pa ẹro ibanisoro rẹ!",
    french: "Éteins ton téléphone portable, s'il-te-plaît",
    english: "Turn off your mobile phone, please.",
    words: ["Jọwọ", "pa", "ẹro", "ibanisoro", "rẹ"],
    audio: "Yo-Jowo_pa_ero_ibanisoro_re",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Nigbawo ni o ma da owo mi pada?",
    french: "Quand est-ce que tu me rendras mon argent?",
    english: "When will you give me back my money?",
    words: ["Nigbawo", "ni", "o", "ma", "da", "owo", "mi", "pada"],
    audio: "Yo-Nigbawo_ni_o_ma_da_owo_mi_pada",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'consignes', 'impératif']
  },
  {
    original: "Mo ni imọran kan",
    french: "J'ai une idée",
    english: "I have an idea.",
    words: ["Mo", "ni", "imọran", "kan"],
    audio: "Yo-Mo_ni_imoran_kan",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Imọran ti o daara pupọ ni eyi",
    french: "C'est une très bonne idée",
    english: "That's a very good idea.",
    words: ["Imọran", "ti", "o", "daara", "pupọ", "ni", "eyi"],
    audio: "Yo-Imoran_ti_o_daara_pupo_ni_eyi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Koko-ọrọ ti mo nifẹ si ni eyi",
    french: "C'est un sujet très intéressant",
    english: "That's a very interesting topic.",
    words: ["Koko-ọrọ", "ti", "mo", "nifẹ", "si", "ni", "eyi"],
    audio: "Yo-Koko-oro_ti_mo_nife_si_ni_eyi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Kini o ro?",
    french: "Qu'en penses-tu?",
    english: "What do you think?",
    words: ["Kini", "o", "ro"],
    audio: "Yo-Kini_o_ro",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Mo fẹ mọ ohun ti o ri si",
    french: "J'aimerais bien connaître ton point de vue",
    english: "I'd like to know your point of view.",
    words: ["Mo", "fẹ", "mọ", "ohun", "ti", "o", "ri", "si"],
    audio: "Yo-Mo_fe_mo_ohun_ti_o_ri_si",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Ọrọ rẹ ko ye mi",
    french: "Je ne comprends pas ce que tu veux dire",
    english: "I don't understand what you mean.",
    words: ["Ọrọ", "rẹ", "ko", "ye", "mi"],
    audio: "Yo-Oro_re_ko_ye_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Ṣe o gba",
    french: "Est-ce que tu es d'accord?",
    english: "Do you agree?",
    words: ["Ṣe", "o", "gba"],
    audio: "Yo-Se_o_gba",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Mi ko ro wipe mo ti ṣe ohunkhoun ti o buru",
    french: "Je ne crois pas avoir fait quelque chose de mal",
    english: "I don't think I did anything wrong.",
    words: ["Mi", "ko", "ro", "wipe", "mo", "ti", "ṣe", "ohunkhoun", "ti", "o", "buru"],
    audio: "Yo-Mi_ko_ro_wipe_mo_ti_se_ohunkhoun_ti_o_buru",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Otitọ lo sọ",
    french: "Tu as raison",
    english: "You're right.",
    words: ["Otitọ", "lo", "sọ"],
    audio: "Yo-Otito_lo_so",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Bẹẹni o ṣe ri",
    french: "C'est vraiment ça",
    english: "That's exactly it.",
    words: ["Bẹẹni", "o", "ṣe", "ri"],
    audio: "Yo-Beeni_o_se_ri",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Maṣe kiyesi ohun ti awọn elo miiran n sọ!",
    french: "Ne te prends pas la tête sur ce que disent les autres",
    english: "Don't worry about what others say!",
    words: ["Maṣe", "kiyesi", "ohun", "ti", "awọn", "elo", "miiran", "n", "sọ"],
    audio: "Yo-Mase_kiyesi_ohun_ti_awon_elo_miiran_n_so",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Kosi ohun ti o le sọ, ko Kan mi",
    french: "Peu importe ce que tu dis, je m'en moque (fous)",
    english: "Whatever you say, I don't care.",
    words: ["Kosi", "ohun", "ti", "o", "le", "sọ", "ko", "Kan", "mi"],
    audio: "Yo-Kosi_ohun_ti_o_le_so_ko_Kan_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Lootọ, nko le ṣe ohunkhoun",
    french: "En effet, je ne peux rien faire",
    english: "Indeed, I can't do anything.",
    words: ["Lootọ", "nko", "le", "ṣe", "ohunkhoun"],
    audio: "Yo-Looto_nko_le_se_ohunkhoun",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Gbolohun rẹ ko gunrege",
    french: "Ta phrase est fausse",
    english: "Your sentence is wrong.",
    words: ["Gbolohun", "rẹ", "ko", "gunrege"],
    audio: "Yo-Gbolohun_re_ko_gunrege",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'discussion']
  },
  {
    original: "Mo fi atẹjiṣẹ ranṣẹ si ọ",
    french: "Je t'ai envoyé un texto",
    english: "I sent you a text.",
    words: ["Mo", "fi", "atẹjiṣẹ", "ranṣẹ", "si", "ọ"],
    audio: "Yo-Mo_fi_atejise_ranse_si_o",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Kini numba erọ ibanisọrọ rẹ?",
    french: "Quel est ton numéro de téléphone?",
    english: "What's your phone number?",
    words: ["Kini", "numba", "erọ", "ibanisọrọ", "rẹ"],
    audio: "Yo-Kini_numba_ero_ibanisoro_re",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Ẹrọ ibanisọrọ mi ti bajẹ",
    french: "Mon téléphone portable ne fonctionne plus",
    english: "My mobile phone doesn't work anymore.",
    words: ["Ẹrọ", "ibanisọrọ", "mi", "ti", "bajẹ"],
    audio: "Yo-Ero_ibanisoro_mi_ti_baje",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Kilo ṣẹlẹ?",
    french: "Qu'est-ce qui s'est passé?",
    english: "What happened?",
    words: ["Kilo", "ṣẹlẹ"],
    audio: "Yo-Kilo_sele",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Nibo ni ẹrọ ibanisọrọ rẹ ti sọnu?",
    french: "Où est-ce que tu as perdu ton portable?",
    english: "Where did you lose your phone?",
    words: ["Nibo", "ni", "ẹrọ", "ibanisọrọ", "rẹ", "ti", "sọnu"],
    audio: "Yo-Nibo_ni_ero_ibanisoro_re_ti_sonu",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Ni ọla, mo ma fi imeeli ranṣẹ si ọ",
    french: "Demain je t'enverrai un e-mail",
    english: "Tomorrow I'll send you an e-mail.",
    words: ["Ni", "ọla", "mo", "ma", "fi", "imeeli", "ranṣẹ", "si", "ọ"],
    audio: "Yo-Ni_ola_mo_ma_fi_imeeli_ranse_si_o",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Mo gbagbe ọrọ igbaniwọle mi",
    french: "J'ai oublié mon mot de passe",
    english: "I forgot my password.",
    words: ["Mo", "gbagbe", "ọrọ", "igbaniwọle", "mi"],
    audio: "Yo-Mo_gbagbe_oro_igbaniwole_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Mo gbagbe orukọ rẹ",
    french: "J'ai oublié ton nom",
    english: "I forgot your name.",
    words: ["Mo", "gbagbe", "orukọ", "rẹ"],
    audio: "Yo-Mo_gbagbe_oruko_re",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'téléphone', 'technologie']
  },
  {
    original: "Ọdẹ ni ọ",
    french: "Tu es un crétin / imbécile",
    english: "You're an idiot.",
    words: ["Ọdẹ", "ni", "ọ"],
    audio: "Yo-Ode_ni_o",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'dispute']
  },
  {
    original: "Kilo gun ọ lati jẹ ba mi sọrọ bẹ yẹn?",
    french: "Comment oses-tu me parler comme ça?",
    english: "How dare you talk to me like that?",
    words: ["Kilo", "gun", "ọ", "lati", "jẹ", "ba", "mi", "sọrọ", "bẹ", "yẹn"],
    audio: "Yo-Kilo_gun_o_lati_je_ba_mi_soro_be_yen",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'dispute']
  },
  {
    original: "Inu mi ko dun si rara",
    french: "Je suis vraiment désolé(e)",
    english: "I'm really sorry.",
    words: ["Inu", "mi", "ko", "dun", "si", "rara"],
    audio: "Yo-Inu_mi_ko_dun_si_rara",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'dispute']
  },
  // --- Au marché ---
  {
    original: "Ṣe ẹ ni iwe itọsọna ounjẹ?",
    french: "Avez-vous un menu?",
    english: "Do you have a menu?",
    words: ["Ṣe", "ẹ", "ni", "iwe", "itọsọna", "ounjẹ"],
    audio: "Yo-Se_e_ni_iwe_itosona_ounje",
    category: 'Au marché',
    tags: ['restaurant', 'commande']
  },
  {
    original: "Oluduro, ẹjọwọ ẹ ba n mu iwe itọsọna ounjẹ",
    french: "Le menu, s'il vous plaît",
    english: "The menu, please.",
    words: ["Oluduro", "ẹjọwọ", "ẹ", "ba", "n", "mu", "iwe", "itọsọna", "ounjẹ"],
    audio: "Yo-Oluduro_ejowo_e_ba_n_mu_iwe_itosona_ounje",
    category: 'Au marché',
    tags: ['restaurant', 'commande']
  },
  {
    original: "Oluduro, ẹjọwọ ẹ fun mi ni iwe iye-owo",
    french: "L'addition, s'il vous plaît",
    english: "The bill, please.",
    words: ["Oluduro", "ẹjọwọ", "ẹ", "fun", "mi", "ni", "iwe", "iye-owo"],
    audio: "Yo-Oluduro_ejowo_e_fun_mi_ni_iwe_iye-owo",
    category: 'Au marché',
    tags: ['restaurant', 'commande']
  },
  {
    original: "Ewo ni ẹ ṣeduro fun?",
    french: "Qu'est-ce que vous me recommandez?",
    english: "What do you recommend?",
    words: ["Ewo", "ni", "ẹ", "ṣeduro", "fun"],
    audio: "Yo-Ewo_ni_e_seduro_fun",
    category: 'Au marché',
    tags: ['restaurant', 'commande']
  },
  {
    original: "Ẹjọwọ ẹ pe wa ṣiwaju lati gba aye silẹ",
    french: "S'il vous plaît, appelez avant pour réserver",
    english: "Please call ahead to book.",
    words: ["Ẹjọwọ", "ẹ", "pe", "wa", "ṣiwaju", "lati", "gba", "aye", "silẹ"],
    audio: "Yo-Ejowo_e_pe_wa_siwaju_lati_gba_aye_sile",
    category: 'Au marché',
    tags: ['restaurant', 'commande']
  },
  {
    original: "Kini o nifẹsi lati ra?",
    french: "Qu'est-ce que tu voudrais acheter?",
    english: "What would you like to buy?",
    words: ["Kini", "o", "nifẹsi", "lati", "ra"],
    audio: "Yo-Kini_o_nifesi_lati_ra",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Mo nifẹ lati ra maapu ilu",
    french: "Je voudrais acheter un plan de la ville",
    english: "I'd like to buy a city map.",
    words: ["Mo", "nifẹ", "lati", "ra", "maapu", "ilu"],
    audio: "Yo-Mo_nife_lati_ra_maapu_ilu",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Mo nifẹ lati ra bata",
    french: "J'aimerais acheter une paire de chaussures",
    english: "I'd like to buy a pair of shoes.",
    words: ["Mo", "nifẹ", "lati", "ra", "bata"],
    audio: "Yo-Mo_nife_lati_ra_bata",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Ẹjọwọ ẹ fi eleyi han mi",
    french: "Montrez-moi celle-ci s'il-vous-plaît",
    english: "Please show me this one.",
    words: ["Ẹjọwọ", "ẹ", "fi", "eleyi", "han", "mi"],
    audio: "Yo-Ejowo_e_fi_eleyi_han_mi",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Elo ni gbogbo ẹ pọ?",
    french: "Combien coûte le tout?",
    english: "How much is it altogether?",
    words: ["Elo", "ni", "gbogbo", "ẹ", "pọ"],
    audio: "Yo-Elo_ni_gbogbo_e_po",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Oga oooo, o wan gan ni oo!",
    french: "Waouh, c'est très cher!",
    english: "Wow, that's very expensive!",
    words: ["Oga", "oooo", "o", "wan", "gan", "ni", "oo"],
    audio: "Yo-Oga_oooo_o_wan_gan_ni_oo",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "O ti wan ju!",
    french: "C'est trop cher",
    english: "It's too expensive.",
    words: ["O", "ti", "wan", "ju"],
    audio: "Yo-O_ti_wan_ju",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Nko ni owo kankan",
    french: "Je n'ai pas d'argent",
    english: "I have no money.",
    words: ["Nko", "ni", "owo", "kankan"],
    audio: "Yo-Nko_ni_owo_kankan",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Apamọwọ mi ti sọnu",
    french: "J'ai perdu mon portefeuille",
    english: "I've lost my wallet.",
    words: ["Apamọwọ", "mi", "ti", "sọnu"],
    audio: "Yo-Apamowo_mi_ti_sonu",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  {
    original: "Mo ma nifẹ lati ṣe paṣipaarọ dọla fun euro",
    french: "Je voudrais changer des dollars en euros",
    english: "I'd like to change dollars into euros.",
    words: ["Mo", "ma", "nifẹ", "lati", "ṣe", "paṣipaarọ", "dọla", "fun", "euro"],
    audio: "Yo-Mo_ma_nife_lati_se_pasipaaro_dola_fun_euro",
    category: 'Au marché',
    tags: ['achats', 'argent', 'prix']
  },
  // --- Voyages ---
  {
    original: "Nibo ni o n lọ?",
    french: "Où vas-tu?",
    english: "Where are you going?",
    words: ["Nibo", "ni", "o", "n", "lọ"],
    audio: "Yo-Nibo_ni_o_n_lo",
    category: 'Voyages',
    tags: ['déplacement', 'orientation']
  },
  {
    original: "Mo ni lati pada si ile nisiyin",
    french: "Je dois rentrer maintenant",
    english: "I have to go home now.",
    words: ["Mo", "ni", "lati", "pada", "si", "ile", "nisiyin"],
    audio: "Yo-Mo_ni_lati_pada_si_ile_nisiyin",
    category: 'Voyages',
    tags: ['déplacement', 'orientation']
  },
  {
    original: "Nibo ni ọna lati de ọfiisi ifiweranṣẹ?",
    french: "Comment aller à la poste?",
    english: "How do I get to the post office?",
    words: ["Nibo", "ni", "ọna", "lati", "de", "ọfiisi", "ifiweranṣẹ"],
    audio: "Yo-Nibo_ni_ona_lati_de_ofiisi_ifiweranse",
    category: 'Voyages',
    tags: ['déplacement', 'orientation']
  },
  {
    original: "Nibo ni ile-itaogun ti o sunmọ ju wa?",
    french: "Où se trouve la pharmacie la plus proche?",
    english: "Where is the nearest pharmacy?",
    words: ["Nibo", "ni", "ile-itaogun", "ti", "o", "sunmọ", "ju", "wa"],
    audio: "Yo-Nibo_ni_ile-itaogun_ti_o_sunmo_ju_wa",
    category: 'Voyages',
    tags: ['déplacement', 'orientation']
  },
  {
    original: "Ṣe ẹnikẹni ti gba ijoko yii?",
    french: "Est-ce que cette place est prise?",
    english: "Is this seat taken?",
    words: ["Ṣe", "ẹnikẹni", "ti", "gba", "ijoko", "yii"],
    audio: "Yo-Se_enikeni_ti_gba_ijoko_yii",
    category: 'Voyages',
    tags: ['déplacement', 'orientation']
  },
  {
    original: "Nibo lo wa?",
    french: "Où es-tu?",
    english: "Where are you?",
    words: ["Nibo", "lo", "wa"],
    audio: "Yo-Nibo_lo_wa",
    category: 'Voyages',
    tags: ['déplacement', 'orientation']
  },
  {
    original: "Mo ni lati lọ",
    french: "Je dois y aller",
    english: "I have to go.",
    words: ["Mo", "ni", "lati", "lọ"],
    audio: "Yo-Mo_ni_lati_lo",
    category: 'Voyages',
    tags: ['déplacement', 'orientation']
  },
  // --- Famille et relations ---
  {
    original: "Mo fẹran rẹ",
    french: "Je t'aime bien",
    english: "I like you.",
    words: ["Mo", "fẹran", "rẹ"],
    audio: "Yo-Mo_feran_re",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Alawada ni ọ",
    french: "Tu es trop drôle",
    english: "You're so funny.",
    words: ["Alawada", "ni", "ọ"],
    audio: "Yo-Alawada_ni_o",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Eni iyi n ọ",
    french: "Tu es génial(e)",
    english: "You're awesome.",
    words: ["Eni", "iyi", "n", "ọ"],
    audio: "Yo-Eni_iyi_n_o",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "O rẹwa pupọ lo birin",
    french: "Tu es très belle",
    english: "You're very beautiful.",
    words: ["O", "rẹwa", "pupọ", "lo", "birin"],
    audio: "Yo-O_rewa_pupo_lo_birin",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "O rẹwa pupọ lọ kunrin",
    french: "Tu es très beau",
    english: "You're very handsome.",
    words: ["O", "rẹwa", "pupọ", "lọ", "kunrin"],
    audio: "Yo-O_rewa_pupo_lo_kunrin",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Ṣe o nifẹ mi!",
    french: "Tu m'aimes?",
    english: "Do you love me?",
    words: ["Ṣe", "o", "nifẹ", "mi"],
    audio: "Yo-Se_o_nife_mi",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Mo nifẹ rẹ pupọ",
    french: "Je t'aime beaucoup",
    english: "I love you very much.",
    words: ["Mo", "nifẹ", "rẹ", "pupọ"],
    audio: "Yo-Mo_nife_re_pupo",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Nko nifẹ re",
    french: "Je ne t'aime pas",
    english: "I don't love you.",
    words: ["Nko", "nifẹ", "re"],
    audio: "Yo-Nko_nife_re",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Aro rẹ sọ mi pupọ",
    french: "Tu me manques beaucoup",
    english: "I miss you a lot.",
    words: ["Aro", "rẹ", "sọ", "mi", "pupọ"],
    audio: "Yo-Aro_re_so_mi_pupo",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Jẹ ki n dimọ ọ",
    french: "Je peux te faire un câlin?",
    english: "Can I give you a hug?",
    words: ["Jẹ", "ki", "n", "dimọ", "ọ"],
    audio: "Yo-Je_ki_n_dimo_o",
    category: 'Famille et relations',
    tags: ['relations', 'sentiments']
  },
  {
    original: "Ṣe o ti ṣe igbeyawo?",
    french: "Est-ce que tu es marié?",
    english: "Are you married?",
    words: ["Ṣe", "o", "ti", "ṣe", "igbeyawo"],
    audio: "Yo-Se_o_ti_se_igbeyawo",
    category: 'Famille et relations',
    tags: ['relations', 'couple']
  },
  {
    original: "Ṣe o ni ọrẹbirin?",
    french: "As-tu une copine?",
    english: "Do you have a girlfriend?",
    words: ["Ṣe", "o", "ni", "ọrẹbirin"],
    audio: "Yo-Se_o_ni_orebirin",
    category: 'Famille et relations',
    tags: ['relations', 'couple']
  },
  {
    original: "Ṣe o ni ọrẹkunrin?",
    french: "As-tu un copain?",
    english: "Do you have a boyfriend?",
    words: ["Ṣe", "o", "ni", "ọrẹkunrin"],
    audio: "Yo-Se_o_ni_orekunrin",
    category: 'Famille et relations',
    tags: ['relations', 'couple']
  },
  {
    original: "Mo ni ala",
    french: "J'ai un rêve",
    english: "I have a dream.",
    words: ["Mo", "ni", "ala"],
    audio: "Yo-Mo_ni_ala",
    category: 'Famille et relations',
    tags: ['relations', 'couple']
  },
  {
    original: "Ala mi ni lati sọ agbaye di ibi ti o dara pupọ",
    french: "Mon rêve c'est de rendre le monde meilleur",
    english: "My dream is to make the world a better place.",
    words: ["Ala", "mi", "ni", "lati", "sọ", "agbaye", "di", "ibi", "ti", "o", "dara", "pupọ"],
    audio: "Yo-Ala_mi_ni_lati_so_agbaye_di_ibi_ti_o_dara_pupo",
    category: 'Famille et relations',
    tags: ['relations', 'couple']
  },
  // --- Urgences et santé ---
  {
    original: "Mo ni ọfikin",
    french: "Je me suis enrhumé(e)",
    english: "I've caught a cold.",
    words: ["Mo", "ni", "ọfikin"],
    audio: "Yo-Mo_ni_ofikin",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Ara mi ko ya",
    french: "Je ne me sens pas bien",
    english: "I don't feel well.",
    words: ["Ara", "mi", "ko", "ya"],
    audio: "Yo-Ara_mi_ko_ya",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Ọfikin ṣe mi ni ana",
    french: "Hier, j'ai attrapé un rhume",
    english: "Yesterday I caught a cold.",
    words: ["Ọfikin", "ṣe", "mi", "ni", "ana"],
    audio: "Yo-Ofikin_se_mi_ni_ana",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Nko ri oruun sun",
    french: "Je n'arrivais pas à dormir",
    english: "I couldn't sleep.",
    words: ["Nko", "ri", "oruun", "sun"],
    audio: "Yo-Nko_ri_oruun_sun",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Mo ni ọfikin, gbogbo aarọ ni mo si fi wukọ",
    french: "Je me suis enrhumé(e) et j'ai toussé toute la matinée",
    english: "I caught a cold and coughed all morning.",
    words: ["Mo", "ni", "ọfikin", "gbogbo", "aarọ", "ni", "mo", "si", "fi", "wukọ"],
    audio: "Yo-Mo_ni_ofikin_gbogbo_aaro_ni_mo_si_fi_wuko",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Mo ni iba ara mi gbona wọ mọkandilogoji iwọn",
    french: "J'ai 39 de fièvre",
    english: "I have a 39-degree fever.",
    words: ["Mo", "ni", "iba", "ara", "mi", "gbona", "wọ", "mọkandilogoji", "iwọn"],
    audio: "Yo-Mo_ni_iba_ara_mi_gbona_wo_mokandilogoji_iwon",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Mo n ṣaare, ara mi o ya rara",
    french: "Je suis malade, je ne me sens pas bien du tout",
    english: "I'm sick, I don't feel well at all.",
    words: ["Mo", "n", "ṣaare", "ara", "mi", "o", "ya", "rara"],
    audio: "Yo-Mo_n_saare_ara_mi_o_ya_rara",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Mo ma n ni ẹfọri nigbagbogbo",
    french: "J'ai souvent mal à la tête",
    english: "I often have headaches.",
    words: ["Mo", "ma", "n", "ni", "ẹfọri", "nigbagbogbo"],
    audio: "Yo-Mo_ma_n_ni_efori_nigbagbogbo",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Inu n ruun mi, kini mo ma ṣe?",
    french: "J'ai mal au ventre, qu'est-ce que je devrais faire ?",
    english: "I have a stomach ache, what should I do?",
    words: ["Inu", "n", "ruun", "mi", "kini", "mo", "ma", "ṣe"],
    audio: "Yo-Inu_n_ruun_mi_kini_mo_ma_se",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Mo ni ireti wi pe o ma wa ni ilera laipẹ!",
    french: "J'espère que tu te sentiras mieux bientôt",
    english: "I hope you feel better soon.",
    words: ["Mo", "ni", "ireti", "wi", "pe", "o", "ma", "wa", "ni", "ilera", "laipẹ"],
    audio: "Yo-Mo_ni_ireti_wi_pe_o_ma_wa_ni_ilera_laipe",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Awọ mi ma n tete ko aisan",
    french: "J'ai une peau très sensible",
    english: "I have very sensitive skin.",
    words: ["Awọ", "mi", "ma", "n", "tete", "ko", "aisan"],
    audio: "Yo-Awo_mi_ma_n_tete_ko_aisan",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Ṣe o ni ewọ kanka?",
    french: "Es-tu allergique à quelque chose?",
    english: "Are you allergic to anything?",
    words: ["Ṣe", "o", "ni", "ewọ", "kanka"],
    audio: "Yo-Se_o_ni_ewo_kanka",
    category: 'Urgences et santé',
    tags: ['santé', 'maladie']
  },
  {
    original: "Ṣe o fẹ iranlọwọ?",
    french: "As-tu besoin d'aide?",
    english: "Do you need help?",
    words: ["Ṣe", "o", "fẹ", "iranlọwọ"],
    audio: "Yo-Se_o_fe_iranlowo",
    category: 'Urgences et santé',
    tags: ['urgence', 'aide']
  },
  {
    original: "Ṣe o le ranmilọwọ?",
    french: "Peux-tu m'aider?",
    english: "Can you help me?",
    words: ["Ṣe", "o", "le", "ranmilọwọ"],
    audio: "Yo-Se_o_le_ranmilowo",
    category: 'Urgences et santé',
    tags: ['urgence', 'aide']
  },
  {
    original: "Jọwọ ṣe o le pe ọkọ alaisan?",
    french: "Peux-tu appeler une ambulance, s'il-te-plaît?",
    english: "Can you call an ambulance, please?",
    words: ["Jọwọ", "ṣe", "o", "le", "pe", "ọkọ", "alaisan"],
    audio: "Yo-Jowo_se_o_le_pe_oko_alaisan",
    category: 'Urgences et santé',
    tags: ['urgence', 'aide']
  },
  {
    original: "Ma binu, emi ko le ran ọ lọwọ",
    french: "Désolé, je ne peux pas t'aider",
    english: "Sorry, I can't help you.",
    words: ["Ma", "binu", "emi", "ko", "le", "ran", "ọ", "lọwọ"],
    audio: "Yo-Ma_binu_emi_ko_le_ran_o_lowo",
    category: 'Urgences et santé',
    tags: ['urgence', 'aide']
  },
  {
    original: "Nibo ni ile-iwosan wa?",
    french: "Où se trouve l'hôpital?",
    english: "Where is the hospital?",
    words: ["Nibo", "ni", "ile-iwosan", "wa"],
    audio: "Yo-Nibo_ni_ile-iwosan_wa",
    category: 'Urgences et santé',
    tags: ['urgence', 'aide']
  },
  // --- Loisirs et culture ---
  {
    original: "Tani ayanfẹ rẹ ninu awọn olukọwe?",
    french: "Qui est ton écrivain préféré?",
    english: "Who is your favourite writer?",
    words: ["Tani", "ayanfẹ", "rẹ", "ninu", "awọn", "olukọwe"],
    audio: "Yo-Tani_ayanfe_re_ninu_awon_olukowe",
    category: 'Loisirs et culture',
    tags: ['loisirs', 'culture']
  },
  {
    original: "Tani ayanfẹ rẹ ninu awọn oṣere?",
    french: "Qui est ton acteur préféré?",
    english: "Who is your favourite actor?",
    words: ["Tani", "ayanfẹ", "rẹ", "ninu", "awọn", "oṣere"],
    audio: "Yo-Tani_ayanfe_re_ninu_awon_osere",
    category: 'Loisirs et culture',
    tags: ['loisirs', 'culture']
  },
  {
    original: "Jẹ ka lọ si ibi ere orí itage",
    french: "Allons au cinéma",
    english: "Let's go to the cinema.",
    words: ["Jẹ", "ka", "lọ", "si", "ibi", "ere", "orí", "itage"],
    audio: "Yo-Je_ka_lo_si_ibi_ere_ori_itage",
    category: 'Loisirs et culture',
    tags: ['loisirs', 'culture']
  },
  {
    original: "Jọwọ pa orin yẹn!",
    french: "Éteins la musique, s'il-te-plaît!",
    english: "Turn off the music, please!",
    words: ["Jọwọ", "pa", "orin", "yẹn"],
    audio: "Yo-Jowo_pa_orin_yen",
    category: 'Loisirs et culture',
    tags: ['loisirs', 'culture']
  },
  {
    original: "Ya aguntan fun mi!",
    french: "Dessine-moi un mouton!",
    english: "Draw me a sheep!",
    words: ["Ya", "aguntan", "fun", "mi"],
    audio: "Yo-Ya_aguntan_fun_mi",
    category: 'Loisirs et culture',
    tags: ['loisirs', 'culture']
  },
  // ===== COMPRENDRE ET SE FAIRE COMPRENDRE (audio) =====
  {
    original: "Mo gbọ́ Yorùbá díẹ̀",
    french: "Je comprends un peu le yoruba",
    english: "I understand a little Yoruba",
    words: ["Mo", "gbọ́", "Yorùbá", "díẹ̀"],
    audio: "Yo-Mo_gbo_Yoruba_die",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Mi ò gbọ́ Yorùbá",
    french: "Je ne comprends pas le yoruba",
    english: "I don't understand Yoruba",
    words: ["Mi", "ò", "gbọ́", "Yorùbá"],
    audio: "Yo-Mi_o_gbo_Yoruba",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Kò tiẹ̀ yé mi rárá",
    french: "Je ne comprends pas du tout",
    english: "I don't understand at all",
    words: ["Kò", "tiẹ̀", "yé", "mi", "rárá"],
    audio: "Yo-Ko_tie_ye_mi_rara",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Kí lẹ wí?",
    french: "Qu’avez-vous dit ? (respectueux)",
    english: "What did you say? (respectful)",
    words: ["Kí", "lẹ", "wí"],
    audio: "Yo-Ki_le_wi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Kí lo wí?",
    french: "Qu’as-tu dit ? (entre amis)",
    english: "What did you say? (between friends)",
    words: ["Kí", "lo", "wí"],
    audio: "Yo-Ki_lo_wi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Ẹ jọ̀ọ́, ẹ ní sùúrù (pẹ̀lú mi)",
    french: "S’il vous plaît, soyez patient (avec moi)",
    english: "Please be patient (with me)",
    words: ["Ẹ", "jọ̀ọ́", "ẹ", "ní", "sùúrù", "pẹ̀lú", "mi"],
    audio: "Yo-E_joo_e_ni_suuru_pelu_mi",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Ẹ jọ̀ọ́, ẹ tun sọ lẹ́ẹ̀kan sí i",
    french: "S’il vous plaît, répétez encore une fois",
    english: "Please say it once more",
    words: ["Ẹ", "jọ̀ọ́", "ẹ", "tun", "sọ", "lẹ́ẹ̀kan", "sí", "i"],
    audio: "Yo-E_joo_e_tun_so_leekan_si_i",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Ó máa yé mi tí ẹ bá rọra sọ̀rọ̀",
    french: "Je comprendrai si vous parlez lentement",
    english: "I'll understand if you speak slowly",
    words: ["Ó", "máa", "yé", "mi", "tí", "ẹ", "bá", "rọra", "sọ̀rọ̀"],
    audio: "Yo-O_maa_ye_mi_ti_e_ba_rora_soro",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  {
    original: "Taló gbọ́ èdè …?",
    french: "Qui comprend la langue … ?",
    english: "Who understands the … language?",
    words: ["Taló", "gbọ́", "èdè"],
    audio: "Yo-Talo_gbo_ede",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'communication', 'compréhension']
  },
  // ===== ÊTRE EN RETARD (audio) =====
  {
    original: "Mo ti ń bọ̀ lọ́nà",
    french: "Je suis en route",
    english: "I'm on my way",
    words: ["Mo", "ti", "ń", "bọ̀", "lọ́nà"],
    audio: "Yo-Mo_ti_n_bo_lona",
    category: 'Voyages',
    tags: ['déplacement', 'retard']
  },
  {
    original: "Mi ò ní pẹ́ d’ẹ̀bẹ̀",
    french: "Je ne vais pas tarder à arriver",
    english: "I won't be long",
    words: ["Mi", "ò", "ní", "pẹ́", "d’ẹ̀bẹ̀"],
    audio: "Yo-Mi_o_ni_pe_debe",
    category: 'Voyages',
    tags: ['déplacement', 'retard']
  },
  {
    original: "Ẹ jọ̀ọ́, ẹ dúró dè mí",
    french: "S’il vous plaît, attendez-moi",
    english: "Please wait for me",
    words: ["Ẹ", "jọ̀ọ́", "ẹ", "dúró", "dè", "mí"],
    category: 'Voyages',
    tags: ['déplacement', 'retard']
  },
  {
    original: "Mo máa d’ẹ̀bẹ̀ ní ìṣẹ́jú mẹ́wàá",
    french: "J’arrive dans 10 minutes",
    english: "I'll be there in 10 minutes",
    words: ["Mo", "máa", "d’ẹ̀bẹ̀", "ní", "ìṣẹ́jú", "mẹ́wàá"],
    audio: "Yo-Mo_maa_debe_ni_iseju_mewaa",
    category: 'Voyages',
    tags: ['déplacement', 'retard']
  },
  {
    original: "Ó máa pẹ́ kí n tó d’ẹ̀bẹ̀",
    french: "Je vais arriver en retard",
    english: "I'm going to be late",
    words: ["Ó", "máa", "pẹ́", "kí", "n", "tó", "d’ẹ̀bẹ̀"],
    audio: "Yo-O_maa_pe_kin_to_debe",
    category: 'Voyages',
    tags: ['déplacement', 'retard']
  },
  {
    original: "Ọkọ̀ mi dákú s’órí títì",
    french: "Ma voiture est tombée en panne sur la route",
    english: "My car broke down on the road",
    words: ["Ọkọ̀", "mi", "dákú", "s’órí", "títì"],
    audio: "Yo-Oko_mi_daku_sori_titi",
    category: 'Voyages',
    tags: ['déplacement', 'retard']
  },
  {
    original: "Ẹjọ́ mi kọ́, mọ́tò mi dákú s’órí títì",
    french: "Ce n’est pas ma faute, ma voiture est tombée en panne sur la route",
    english: "It's not my fault, my car broke down on the road",
    words: ["Ẹjọ́", "mi", "kọ́", "mọ́tò", "mi", "dákú", "s’órí", "títì"],
    audio: "Yo-Ejo_mi_ko_moto_mi_daku_sori_titi",
    category: 'Voyages',
    tags: ['déplacement', 'retard']
  },
  {
    original: "Ó pẹ́ kí n tó kúrò n’ílé",
    french: "Je suis parti(e) tard de la maison",
    english: "I left home late",
    words: ["Ó", "pẹ́", "kí", "n", "tó", "kúrò", "n’ílé"],
    audio: "Yo-O_pe_kin_to_kuro_nile",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'retard']
  },
  {
    original: "Ó pẹ́ kí n tó jí",
    french: "Je me suis réveillé(e) tard",
    english: "I woke up late",
    words: ["Ó", "pẹ́", "kí", "n", "tó", "jí"],
    audio: "Yo-O_pe_kin_to_ji",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'retard']
  },
  {
    original: "Mo sùn gbàgbé",
    french: "Je ne me suis pas réveillé(e) / J’ai trop dormi",
    english: "I overslept",
    words: ["Mo", "sùn", "gbàgbé"],
    audio: "Yo-Mo_sun_gbagbe",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'retard']
  },
  {
    original: "Ẹjọ́ mi kọ́",
    french: "Ce n’est pas ma faute",
    english: "It's not my fault",
    words: ["Ẹjọ́", "mi", "kọ́"],
    audio: "Yo-Ejo_mi_ko",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'retard']
  },
  // ===== SANTÉ — DOULEURS ET SYMPTÔMES (audio) =====
  {
    original: "Eyín ń ro mi",
    french: "J’ai mal aux dents",
    english: "I have a toothache",
    words: ["Eyín", "ń", "ro", "mi"],
    audio: "Yo-Eyin_n_ro_mi",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'symptôme']
  },
  {
    original: "Ojú ń ta mi",
    french: "J’ai les yeux qui piquent",
    english: "My eyes are stinging",
    words: ["Ojú", "ń", "ta", "mi"],
    audio: "Yo-Oju_n_ta_mi",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'symptôme']
  },
  {
    original: "Ara mi ń gbóná",
    french: "J’ai de la fièvre / Je me sens chaud",
    english: "I have a fever / I feel hot",
    words: ["Ara", "mi", "ń", "gbóná"],
    audio: "Yo-Ara_mi_n_gbona",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'symptôme']
  },
  {
    original: "Òyì ń kọ́ mi",
    french: "J’ai la tête qui tourne",
    english: "I feel dizzy",
    words: ["Òyì", "ń", "kọ́", "mi"],
    audio: "Yo-Oyi_n_ko_mi",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'symptôme']
  },
  {
    original: "Mo ní ibà",
    french: "J’ai de la fièvre",
    english: "I have a fever",
    words: ["Mo", "ní", "ibà"],
    audio: "Yo-Mo_ni_iba",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'symptôme']
  },
  {
    original: "Mo ní eéwo",
    french: "J’ai un furoncle",
    english: "I have a boil",
    words: ["Mo", "ní", "eéwo"],
    audio: "Yo-Mo_ni_eewo",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'symptôme']
  },
  {
    original: "Mo ń ṣẹ̀jẹ̀",
    french: "Je saigne",
    english: "I'm bleeding",
    words: ["Mo", "ń", "ṣẹ̀jẹ̀"],
    audio: "Yo-Mo_n_seje",
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'symptôme']
  },
  // ===== EXPRIMER SON DÉSACCORD (audio) =====
  {
    original: "Mi ò gbà",
    french: "Je ne suis pas d’accord",
    english: "I don't agree",
    words: ["Mi", "ò", "gbà"],
    audio: "Yo-Mi_o_gba",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Mi ò f’ara mọ́",
    french: "Je ne suis pas d’accord (je n’y adhère pas)",
    english: "I don't go along with it",
    words: ["Mi", "ò", "f’ara", "mọ́"],
    audio: "Yo-Mi_o_fara_mo",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Mo kọ̀",
    french: "Je refuse",
    english: "I refuse",
    words: ["Mo", "kọ̀"],
    audio: "Yo-Mo_ko",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Mi ò fẹ́ ẹ",
    french: "Je n’en veux pas",
    english: "I don't want it",
    words: ["Mi", "ò", "fẹ́", "ẹ"],
    audio: "Yo-Mi_o_fe_e",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Mi ò fẹ́ràn ẹ",
    french: "Je n’aime pas ça",
    english: "I don't like it",
    words: ["Mi", "ò", "fẹ́ràn", "ẹ"],
    audio: "Yo-Mi_o_feran_e",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Èrò yìí ò dáa",
    french: "Ce n’est pas une bonne idée",
    english: "This isn't a good idea",
    words: ["Èrò", "yìí", "ò", "dáa"],
    audio: "Yo-Ero_yii_o_daa",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Ọ̀rọ̀ wa kò dọ́gba",
    french: "Nous ne sommes pas sur la même longueur d’onde",
    english: "We're not on the same wavelength",
    words: ["Ọ̀rọ̀", "wa", "kò", "dọ́gba"],
    audio: "Yo-Oro_wa_ko_dogba",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Ọ̀rọ̀ tèmi àti tiẹ̀ ò b’ára wọn mu",
    french: "Nos avis ne concordent pas",
    english: "Our opinions don't match",
    words: ["Ọ̀rọ̀", "tèmi", "àti", "tiẹ̀", "ò", "b’ára", "wọn", "mu"],
    audio: "Yo-Oro_temi_ati_tie_o_bara_won_mu",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Ó ń ṣe mí ní iyè méjì",
    french: "J’ai des doutes (à ce sujet)",
    english: "I have doubts (about it)",
    words: ["Ó", "ń", "ṣe", "mí", "ní", "iyè", "méjì"],
    audio: "Yo-O_n_se_mi_ni_iye_meji",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  {
    original: "Kò dá mi lójú kedere",
    french: "Je n’en suis pas absolument certain(e)",
    english: "I'm not absolutely sure",
    words: ["Kò", "dá", "mi", "lójú", "kedere"],
    audio: "Yo-Ko_da_mi_loju_kedere",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'opinion', 'désaccord']
  },
  // ===== LE GOÛT DES ALIMENTS (audio) =====
  {
    original: "Ó kan bíi ọsàn wẹ́wẹ́",
    french: "C’est acide comme du citron vert",
    english: "It's sour like lime",
    words: ["Ó", "kan", "bíi", "ọsàn", "wẹ́wẹ́"],
    audio: "Yo-O_kan_bii_osan_wewe",
    category: 'Au marché',
    tags: ['nourriture', 'goût', 'adjectifs']
  },
  {
    original: "Àgbo yẹn korò",
    french: "Cette tisane (àgbo) est amère",
    english: "That herbal drink (àgbo) is bitter",
    words: ["Àgbo", "yẹn", "korò"],
    audio: "Yo-Agbo_yen_koro",
    category: 'Au marché',
    tags: ['nourriture', 'goût', 'adjectifs']
  },
  {
    original: "Iyán tí mo rà le",
    french: "L’igname pilée que j’ai achetée était dure",
    english: "The pounded yam I bought was hard",
    words: ["Iyán", "tí", "mo", "rà", "le"],
    audio: "Yo-Iyan_ti_mo_ra_le",
    category: 'Au marché',
    tags: ['nourriture', 'goût', 'adjectifs']
  },
  // ===== POSER DES QUESTIONS (audio) =====
  {
    original: "Kí ni orúkọ ẹ?",
    french: "Comment tu t’appelles ?",
    english: "What's your name?",
    words: ["Kí", "ni", "orúkọ", "ẹ"],
    audio: "Yo-Ki_ni_oruko_e",
    category: 'Présentation',
    tags: ['nom', 'présentation', 'questions']
  },
  {
    original: "Mo ń gbé ní …",
    french: "J’habite à …",
    english: "I live in …",
    words: ["Mo", "ń", "gbé", "ní"],
    audio: "Yo-Mo_n_gbe_ni",
    category: 'Présentation',
    tags: ['présentation', 'lieu']
  },
  {
    original: "Nígbà wo lo ń jáde?",
    french: "Quand sors-tu ?",
    english: "When are you going out?",
    words: ["Nígbà", "wo", "lo", "ń", "jáde"],
    audio: "Yo-Nigba_wo_lo_n_jade",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions', 'temps']
  },
  {
    original: "Tani yẹn?",
    french: "Qui est-ce ?",
    english: "Who is that?",
    words: ["Tani", "yẹn"],
    audio: "Yo-Tani_yen",
    category: 'Vie quotidienne',
    tags: ['quotidien', 'questions']
  },
  // ===== LES COULEURS (audio) =====
  {
    original: "Adìẹ funfun",
    french: "Une poule blanche",
    english: "A white hen",
    words: ["Adìẹ", "funfun"],
    audio: "Yo-Adie_funfun",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Bàtà dúdú",
    french: "Une chaussure noire",
    english: "A black shoe",
    words: ["Bàtà", "dúdú"],
    audio: "Yo-Bata_dudu",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Ọkọ̀ pupa",
    french: "Une voiture rouge",
    english: "A red car",
    words: ["Ọkọ̀", "pupa"],
    audio: "Yo-Oko_pupa",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Ife aláwọ̀ ọsàn",
    french: "Un gobelet orange",
    english: "An orange cup",
    words: ["Ife", "aláwọ̀", "ọsàn"],
    audio: "Yo-Ife_alawo_osan",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Aṣọ aláwọ̀ òfééfé",
    french: "Un tissu jaune",
    english: "A yellow cloth",
    words: ["Aṣọ", "aláwọ̀", "òfééfé"],
    audio: "Yo-Aso_alawo_ofeefe",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Kòkòrò aláwọ̀ ewé",
    french: "Un insecte vert",
    english: "A green insect",
    words: ["Kòkòrò", "aláwọ̀", "ewé"],
    audio: "Yo-Kokoro_alawo_ewe",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Gèlè aláwọ̀ aró",
    french: "Un foulard (gèlè) bleu",
    english: "A blue headwrap (gèlè)",
    words: ["Gèlè", "aláwọ̀", "aró"],
    audio: "Yo-Gele_alawo_aro",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Àpò aláwọ̀ igi",
    french: "Un sac marron",
    english: "A brown bag",
    words: ["Àpò", "aláwọ̀", "igi"],
    audio: "Yo-Apo_alawo_igi",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Ìwé aláwọ̀ fàdákà",
    french: "Un livre argenté",
    english: "A silver book",
    words: ["Ìwé", "aláwọ̀", "fàdákà"],
    audio: "Yo-Iwe_alawo_fadaka",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  },
  {
    original: "Sálúbàtà aláwọ̀ wúrà",
    french: "Des sandales dorées",
    english: "Golden sandals",
    words: ["Sálúbàtà", "aláwọ̀", "wúrà"],
    audio: "Yo-Salubata_alawo_wura",
    category: 'Au marché',
    tags: ['couleurs', 'objets']
  }
];

// Fonctions utilitaires
export const getSentencesByCategory = (category: keyof typeof SENTENCE_CATEGORIES) =>
  sentencesToConstruct.filter(sentence => sentence.category === category);

export const getSentencesByTags = (tags: string[]) =>
  sentencesToConstruct.filter(sentence =>
    tags.some(tag => sentence.tags.includes(tag))
  );

export const getCategories = () => SENTENCE_CATEGORIES;

export default sentencesToConstruct;
